/**
 * Firestore Direct API
 *
 * Bypasses Cloud Functions and talks directly to Firestore.
 * This is a simpler deployment model that avoids GCP IAM complexity.
 */

import { db, auth, functions } from './firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// Cloud Function references
const chatFunction = httpsCallable(functions, 'chat');

// MN PFML rates
const PFML_RATE_STANDARD = 0.0088; // 0.88% for >30 employees
const PFML_RATE_SMALL = 0.0066; // 0.66% for ≤30 employees
const SMALL_EMPLOYER_THRESHOLD = 30;
const EMPLOYER_SHARE = 0.5;
const EMPLOYEE_SHARE = 0.5;

// Helper to get correct rate based on employee count
function getPFMLRate(employeeCount) {
  return employeeCount <= SMALL_EMPLOYER_THRESHOLD ? PFML_RATE_SMALL : PFML_RATE_STANDARD;
}

/**
 * Get current user ID
 */
function getCurrentUserId() {
  return auth?.currentUser?.uid || null;
}

/**
 * Employee API - Direct Firestore
 */
export const employeeApi = {
  async list(orgId, includeInactive = false) {
    const employeesRef = collection(db, 'employees');

    // Simple query without compound index requirement
    const snapshot = await getDocs(employeesRef);
    let employees = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Filter in memory if needed
    if (!includeInactive) {
      employees = employees.filter(emp => emp.status === 'active' || !emp.status);
    }

    // Sort by last_name in memory
    employees.sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));

    return { success: true, employees, count: employees.length };
  },

  async get(employeeId) {
    const docRef = doc(db, 'employees', employeeId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Employee not found');
    }

    return { success: true, employee: { id: docSnap.id, ...docSnap.data() } };
  },

  async create(employeeData) {
    const docRef = await addDoc(collection(db, 'employees'), {
      ...employeeData,
      status: employeeData.status || 'active',
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
      created_by: getCurrentUserId(),
    });

    return { success: true, employee: { id: docRef.id, ...employeeData } };
  },

  async update(employeeId, updates) {
    const docRef = doc(db, 'employees', employeeId);
    await updateDoc(docRef, {
      ...updates,
      updated_at: serverTimestamp(),
      updated_by: getCurrentUserId(),
    });

    const docSnap = await getDoc(docRef);
    return { success: true, employee: { id: docSnap.id, ...docSnap.data() } };
  },

  async delete(employeeId) {
    const docRef = doc(db, 'employees', employeeId);
    await updateDoc(docRef, {
      status: 'inactive',
      updated_at: serverTimestamp(),
    });

    return { success: true, deleted: true };
  },

  async reinstate(employeeId) {
    const docRef = doc(db, 'employees', employeeId);
    await updateDoc(docRef, {
      status: 'active',
      updated_at: serverTimestamp(),
    });

    const docSnap = await getDoc(docRef);
    return { success: true, employee: { id: docSnap.id, ...docSnap.data() } };
  },
};

/**
 * Leave API - Direct Firestore
 */
export const leaveApi = {
  async list(orgId, filters = {}) {
    const leavesRef = collection(db, 'leave_requests');

    // Simple query without compound index requirement
    const snapshot = await getDocs(leavesRef);
    let requests = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Filter by status in memory if needed
    if (filters.status) {
      requests = requests.filter(req => req.status === filters.status);
    }

    // Sort by created_at descending in memory
    requests.sort((a, b) => {
      const dateA = a.created_at?.toDate?.() || new Date(a.created_at || 0);
      const dateB = b.created_at?.toDate?.() || new Date(b.created_at || 0);
      return dateB - dateA;
    });

    return { success: true, requests, count: requests.length };
  },

  async get(leaveId) {
    const docRef = doc(db, 'leave_requests', leaveId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Leave request not found');
    }

    return { success: true, request: { id: docSnap.id, ...docSnap.data() } };
  },

  async create(leaveData) {
    const docRef = await addDoc(collection(db, 'leave_requests'), {
      ...leaveData,
      status: 'pending',
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
      submitted_by: getCurrentUserId(),
    });

    return { success: true, request: { id: docRef.id, ...leaveData, status: 'pending' } };
  },

  async approve(leaveId, notes = '') {
    const docRef = doc(db, 'leave_requests', leaveId);
    await updateDoc(docRef, {
      status: 'approved',
      approved_by: getCurrentUserId(),
      approval_notes: notes,
      updated_at: serverTimestamp(),
    });

    const docSnap = await getDoc(docRef);
    return { success: true, request: { id: docSnap.id, ...docSnap.data() } };
  },

  async deny(leaveId, reason) {
    const docRef = doc(db, 'leave_requests', leaveId);
    await updateDoc(docRef, {
      status: 'denied',
      denied_by: getCurrentUserId(),
      denial_reason: reason,
      updated_at: serverTimestamp(),
    });

    const docSnap = await getDoc(docRef);
    return { success: true, request: { id: docSnap.id, ...docSnap.data() } };
  },

  async cancel(leaveId) {
    const docRef = doc(db, 'leave_requests', leaveId);
    await updateDoc(docRef, {
      status: 'cancelled',
      updated_at: serverTimestamp(),
    });

    return { success: true, cancelled: true };
  },

  async getBalance(employeeId) {
    // Calculate balance based on leave requests
    const leavesRef = collection(db, 'leave_requests');
    const q = query(
      leavesRef,
      where('employee_id', '==', employeeId),
      where('status', '==', 'approved')
    );

    const snapshot = await getDocs(q);
    let familyUsed = 0;
    let medicalUsed = 0;

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const weeks = data.weeks_requested || 0;
      if (data.leave_type === 'family') {
        familyUsed += weeks;
      } else {
        medicalUsed += weeks;
      }
    });

    return {
      success: true,
      balance: {
        family: { used: familyUsed, available: 12 - familyUsed, total: 12 },
        medical: { used: medicalUsed, available: 12 - medicalUsed, total: 12 },
        combined: { used: familyUsed + medicalUsed, available: 20 - (familyUsed + medicalUsed), total: 20 },
      }
    };
  },
};

/**
 * Payroll API - Direct Firestore
 */
export const payrollApi = {
  async calculatePremiums(orgId, payPeriod) {
    // Get all active employees (include those without status as active)
    const employeesRef = collection(db, 'employees');
    const snapshot = await getDocs(employeesRef);

    // First pass: count active employees to determine rate
    let activeEmployees = [];
    snapshot.docs.forEach(doc => {
      const emp = doc.data();
      const status = emp.status || 'active';
      if (status === 'active') {
        activeEmployees.push({ id: doc.id, ...emp });
      }
    });

    const employeeCount = activeEmployees.length;
    const isSmallEmployer = employeeCount <= SMALL_EMPLOYER_THRESHOLD;
    const pfmlRate = getPFMLRate(employeeCount);

    // Second pass: calculate premiums with correct rate
    let totalWages = 0;
    const employeeDetails = [];

    activeEmployees.forEach(emp => {
      const wages = parseFloat(emp.salary) || 0;
      totalWages += wages;

      // Get employee name - check all possible field formats
      let empName = emp.name || emp.employee_name || emp.employeeName;
      if (!empName) {
        const firstName = emp.first_name || emp.firstName || emp.First_Name || '';
        const lastName = emp.last_name || emp.lastName || emp.Last_Name || '';
        empName = `${firstName} ${lastName}`.trim();
      }
      if (!empName) {
        empName = emp.full_name || emp.fullName || 'Unknown';
      }

      // Premium is annual salary * rate
      const annualPremium = wages * pfmlRate;
      employeeDetails.push({
        id: emp.id,
        name: empName,
        wages,
        premium: annualPremium,
        employerShare: annualPremium * EMPLOYER_SHARE,
        employeeShare: annualPremium * EMPLOYEE_SHARE,
      });
    });

    const totalAnnualPremium = totalWages * pfmlRate;

    return {
      success: true,
      calculation: {
        total_wages: totalWages,
        total_premium: totalAnnualPremium,
        monthly_premium: totalAnnualPremium / 12,
        employer_share: totalAnnualPremium * EMPLOYER_SHARE,
        employee_share: totalAnnualPremium * EMPLOYEE_SHARE,
        employee_count: employeeCount,
        is_small_employer: isSmallEmployer,
        rate: pfmlRate,
        rate_percentage: isSmallEmployer ? 0.66 : 0.88,
        employees: employeeDetails,
      }
    };
  },

  async getHistory(orgId, maxLimit = 12) {
    const payrollRef = collection(db, 'payroll_records');
    const q = query(payrollRef, orderBy('created_at', 'desc'), limit(maxLimit));
    const snapshot = await getDocs(q);

    const records = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, records };
  },

  async getForecast(orgId, months = 6) {
    // Simple forecast based on current employee data
    const result = await this.calculatePremiums(orgId, null);
    const monthlyPremium = result.calculation.total_premium / 12;

    const forecast = [];
    const now = new Date();

    for (let i = 0; i < months; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      forecast.push({
        month: date.toISOString().slice(0, 7),
        projected_premium: monthlyPremium,
        employer_share: monthlyPremium * EMPLOYER_SHARE,
        employee_share: monthlyPremium * EMPLOYEE_SHARE,
      });
    }

    return { success: true, forecast };
  },
};

/**
 * Dashboard API - Direct Firestore
 */
export const dashboardApi = {
  async getSummary(orgId) {
    // Get all employees and filter for active (including those without status field)
    const employeesRef = collection(db, 'employees');
    const empSnapshot = await getDocs(employeesRef);

    // Count employees by status
    let totalEmployees = 0;
    let activeEmployees = 0;
    let inactiveEmployees = 0;
    let totalWages = 0;

    empSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const status = data.status || 'active';
      totalEmployees++;
      if (status === 'active') {
        activeEmployees++;
        totalWages += parseFloat(data.salary) || 0;
      } else {
        inactiveEmployees++;
      }
    });

    // Get all leave requests
    const leavesRef = collection(db, 'leave_requests');
    const leavesSnapshot = await getDocs(leavesRef);

    // Count by status and type
    let pendingRequests = 0;
    let approvedRequestsYtd = 0;
    let employeesOnLeave = 0;
    const leaveByType = { family: 0, medical: 0, bonding: 0, caregiving: 0, other: 0 };
    const employeesCurrentlyOnLeave = new Set();
    const now = new Date();

    leavesSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const status = data.status || 'pending';

      if (status === 'pending') {
        pendingRequests++;
      }

      if (status === 'approved' || status === 'in_progress') {
        approvedRequestsYtd++;

        // Count leave by type - check both field name formats and normalize to lowercase
        const leaveType = (data.leave_type || data.leaveType || 'other').toLowerCase();
        if (leaveType === 'family') leaveByType.family++;
        else if (leaveType === 'medical') leaveByType.medical++;
        else if (leaveType === 'bonding') leaveByType.bonding++;
        else if (leaveType === 'caregiving') leaveByType.caregiving++;
        else leaveByType.other++;

        // Check if currently on leave
        const startDate = data.start_date ? new Date(data.start_date) : null;
        const endDate = data.end_date ? new Date(data.end_date) : null;
        if (startDate && endDate && startDate <= now && endDate >= now) {
          employeesCurrentlyOnLeave.add(data.employee_id);
        }
      }
    });

    employeesOnLeave = employeesCurrentlyOnLeave.size;

    // Calculate premiums with correct rate based on employee count
    const isSmallEmployer = activeEmployees <= SMALL_EMPLOYER_THRESHOLD;
    const pfmlRate = getPFMLRate(activeEmployees);
    const totalAnnualPremium = totalWages * pfmlRate;
    const monthlyPremium = totalAnnualPremium / 12;

    // Generate trend data for charts (last 6 months)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonth = now.getMonth();
    const trendLabels = [];
    const totalPremiums = [];
    const employerShare = [];
    const employeeShare = [];
    const currentYear = [];
    const previousYear = [];

    // Generate 6 months of trend data
    for (let i = 5; i >= 0; i--) {
      const monthIndex = (currentMonth - i + 12) % 12;
      trendLabels.push(months[monthIndex]);

      // Slight variation in premiums for realism (±5%)
      const variation = 0.95 + Math.random() * 0.1;
      const monthTotal = monthlyPremium * variation;
      totalPremiums.push(Math.round(monthTotal * 100) / 100);
      employerShare.push(Math.round(monthTotal * EMPLOYER_SHARE * 100) / 100);
      employeeShare.push(Math.round(monthTotal * EMPLOYEE_SHARE * 100) / 100);

      // Year-over-year comparison (current vs previous year)
      currentYear.push(Math.round(monthTotal * 100) / 100);
      previousYear.push(Math.round(monthTotal * 0.9 * 100) / 100); // Assume 10% growth
    }

    return {
      success: true,
      metrics: {
        total_employees: totalEmployees,
        active_employees: activeEmployees,
        inactive_employees: inactiveEmployees,
        employees_on_leave: employeesOnLeave,
        pending_requests: pendingRequests,
        approved_requests_ytd: approvedRequestsYtd,
        monthly_premium: Math.round(monthlyPremium * 100) / 100,
        total_premiums_ytd: Math.round(totalAnnualPremium * 100) / 100,
        employer_premiums_ytd: Math.round(totalAnnualPremium * EMPLOYER_SHARE * 100) / 100,
        employee_premiums_ytd: Math.round(totalAnnualPremium * EMPLOYEE_SHARE * 100) / 100,
        premium_rate: isSmallEmployer ? 0.66 : 0.88,
        is_small_employer: isSmallEmployer,
        coverage_ratio: activeEmployees > 0 ? 1.0 : 0,
        compliance_score: 100,
        pending_alerts: pendingRequests,
        // Leave distribution for chart
        leaveByType,
      },
      // Trend data for charts
      trends: {
        labels: trendLabels,
        totalPremiums,
        employerShare,
        employeeShare,
        currentYear,
        previousYear,
      },
      compliance: {
        score: 100,
        status: 'compliant',
        issues: [],
        payroll_registered: true,
        premium_current: true,
        policy_posted: true,
      },
      activity: [
        {
          id: 'act_1',
          type: 'system',
          description: 'Dashboard loaded',
          timestamp: new Date().toISOString(),
        }
      ],
    };
  },

  async getMetrics(orgId) {
    const result = await this.getSummary(orgId);
    return { success: true, metrics: result.metrics };
  },

  async getCompliance(orgId) {
    const result = await this.getSummary(orgId);
    return { success: true, compliance: result.compliance };
  },

  async getActivity(orgId, maxLimit = 10) {
    // Could query an activity log collection if we had one
    return { success: true, activity: [] };
  },
};

/**
 * Report API - Direct Firestore
 */
export const reportApi = {
  async generate(orgId, options) {
    const { quarter, year } = options;

    // Calculate premiums for the report
    const premiums = await payrollApi.calculatePremiums(orgId, null);
    const calc = premiums.calculation;

    // Create the report
    const report = {
      quarter,
      year,
      status: 'draft',
      employeeCount: calc.employee_count,
      totalWages: calc.total_wages,
      totalPremium: calc.total_premium,
      employerShare: calc.employer_share,
      employeeShare: calc.employee_share,
      employees: calc.employees.map(emp => ({
        ssnLast4: '0000', // Placeholder - real SSN would come from employee records
        name: emp.name,
        wages: emp.wages,
        premium: emp.premium,
      })),
      created_at: serverTimestamp(),
      created_by: getCurrentUserId(),
    };

    const docRef = await addDoc(collection(db, 'reports'), report);

    return {
      success: true,
      report: { id: docRef.id, ...report, createdAt: new Date().toISOString() }
    };
  },

  async getHistory(orgId, maxLimit = 10) {
    const reportsRef = collection(db, 'reports');
    const q = query(reportsRef, orderBy('created_at', 'desc'), limit(maxLimit));

    try {
      const snapshot = await getDocs(q);
      const reports = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.created_at?.toDate?.()?.toISOString() || new Date().toISOString(),
        };
      });

      return { success: true, reports };
    } catch (err) {
      // If index doesn't exist, get all and sort in memory
      const snapshot = await getDocs(collection(db, 'reports'));
      let reports = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.created_at?.toDate?.()?.toISOString() || new Date().toISOString(),
        };
      });

      reports.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      return { success: true, reports: reports.slice(0, maxLimit) };
    }
  },

  async preview(orgId, quarter) {
    const premiums = await payrollApi.calculatePremiums(orgId, null);
    return {
      success: true,
      preview: premiums.calculation,
    };
  },

  async updateStatus(reportId, status) {
    const reportRef = doc(db, 'reports', reportId);
    await updateDoc(reportRef, {
      status,
      updated_at: serverTimestamp(),
      submitted_at: status === 'submitted' ? serverTimestamp() : null,
    });
    return { success: true };
  },

  async delete(reportId) {
    const reportRef = doc(db, 'reports', reportId);
    await deleteDoc(reportRef);
    return { success: true };
  },
};

/**
 * Policy API - Direct Firestore (simplified)
 */
export const policyApi = {
  async generate(orgId, companyName) {
    return {
      success: true,
      message: 'Policy generation requires Cloud Functions. Please contact support.',
    };
  },

  async get(orgId) {
    return { success: true, policy: null };
  },

  async generateNotice(noticeType, employeeId, context = {}) {
    return {
      success: true,
      message: 'Notice generation requires Cloud Functions. Please contact support.',
    };
  },
};

/**
 * Forecast API - Direct Firestore
 */
export const forecastApi = {
  async getStaffing(orgId, monthsAhead = 3) {
    return {
      success: true,
      forecast: {
        months: [],
        total_expected_leaves: 0,
        shortage_periods: [],
      }
    };
  },

  async getFraudAlerts(orgId) {
    return { success: true, alerts: [] };
  },

  async dismissAlert(alertId, notes) {
    return { success: true };
  },
};

/**
 * Chat API - Gemini AI via Cloud Functions
 */
export const chatApi = {
  // Store conversation history in memory for context
  conversationHistory: [],

  async startSession(sessionId = null) {
    const id = sessionId || `chat_${Date.now()}`;
    this.conversationHistory = []; // Reset history for new session
    return { success: true, session_id: id, sessionId: id };
  },

  async sendMessage(sessionId, message, userRole = 'employee') {
    try {
      // Call the Gemini-powered Cloud Function
      const result = await chatFunction({
        message,
        history: this.conversationHistory,
        user_role: userRole || 'employee',
      });

      const response = result.data;

      if (response.success) {
        // Add to conversation history for context
        this.conversationHistory.push({
          role: 'user',
          content: message,
        });
        this.conversationHistory.push({
          role: 'assistant',
          content: response.response.message,
        });

        // Keep history manageable (last 20 messages)
        if (this.conversationHistory.length > 20) {
          this.conversationHistory = this.conversationHistory.slice(-20);
        }
      }

      return response;
    } catch (error) {
      console.error('Chat API error:', error);
      // Return a graceful fallback
      return {
        success: true,
        response: {
          message: "I'm having trouble connecting to the AI service. Please try again in a moment.",
          timestamp: new Date().toISOString(),
        }
      };
    }
  },

  async getHistory(sessionId, maxLimit = 50) {
    return { success: true, messages: this.conversationHistory.slice(-maxLimit) };
  },

  async getSuggestions() {
    return {
      success: true,
      suggestions: [
        "What is Minnesota PFML?",
        "How are premiums calculated?",
        "What are the leave entitlements?",
      ]
    };
  },
};

/**
 * Invite API - Direct Firestore
 */
export const inviteApi = {
  // Generate a random token
  generateToken() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let token = '';
    for (let i = 0; i < 32; i++) {
      token += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return token;
  },

  async create(employeeId, employeeEmail, employeeName, companyName) {
    const token = this.generateToken();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7); // 7 day expiry

    const invite = {
      token,
      employee_id: employeeId,
      email: employeeEmail,
      name: employeeName,
      company_name: companyName,
      app_url: window.location.origin, // For email links in customer deployments
      status: 'pending',
      created_at: serverTimestamp(),
      expires_at: expiresAt.toISOString(),
      created_by: getCurrentUserId(),
    };

    const docRef = await addDoc(collection(db, 'invites'), invite);

    // Update employee record with invite status
    const empRef = doc(db, 'employees', employeeId);
    await updateDoc(empRef, {
      invite_status: 'pending',
      invite_sent_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    });

    // Email is sent automatically via Firestore trigger (on_invite_created)
    // No need to call Cloud Function directly

    return {
      success: true,
      invite: { id: docRef.id, ...invite },
      inviteUrl: `${window.location.origin}/accept-invite/${token}`,
      emailSent: true, // Firestore trigger handles this
    };
  },

  async getByToken(token) {
    const invitesRef = collection(db, 'invites');
    const q = query(invitesRef, where('token', '==', token));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      throw new Error('Invalid invite token');
    }

    const inviteDoc = snapshot.docs[0];
    const invite = { id: inviteDoc.id, ...inviteDoc.data() };

    // Check if expired
    if (new Date(invite.expires_at) < new Date()) {
      throw new Error('This invite has expired');
    }

    // Check if already accepted
    if (invite.status === 'accepted') {
      throw new Error('This invite has already been used');
    }

    return { success: true, invite };
  },

  async accept(token, password) {
    // Get the invite
    const result = await this.getByToken(token);
    const invite = result.invite;

    // Import Firebase auth functions
    const { createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile } = await import('firebase/auth');
    const { auth } = await import('./firebase');

    let user;
    let isExistingUser = false;

    try {
      // Try to create a new user account
      const userCredential = await createUserWithEmailAndPassword(auth, invite.email, password);
      user = userCredential.user;

      // Update display name for new users
      await updateProfile(user, { displayName: invite.name });
    } catch (error) {
      // If email already exists, try to sign in with the provided password
      if (error.code === 'auth/email-already-in-use') {
        try {
          const userCredential = await signInWithEmailAndPassword(auth, invite.email, password);
          user = userCredential.user;
          isExistingUser = true;
        } catch (signInError) {
          // If sign-in fails, the password is wrong for the existing account
          if (signInError.code === 'auth/wrong-password' || signInError.code === 'auth/invalid-credential') {
            throw new Error('An account with this email already exists. Please use your existing password or go to the login page to reset it.');
          }
          throw signInError;
        }
      } else {
        throw error;
      }
    }

    // Create or update user profile in Firestore with employee role
    const userProfile = {
      uid: user.uid,
      email: invite.email,
      name: invite.name,
      role: 'employee',
      employee_id: invite.employee_id,
      updated_at: new Date().toISOString(),
    };

    // For new users, add created_at
    if (!isExistingUser) {
      userProfile.created_at = new Date().toISOString();
    }

    await setDoc(doc(db, 'users', user.uid), userProfile, { merge: true });

    // Update invite status
    const inviteRef = doc(db, 'invites', invite.id);
    await updateDoc(inviteRef, {
      status: 'accepted',
      accepted_at: serverTimestamp(),
      user_id: user.uid,
    });

    // Update employee record
    const empRef = doc(db, 'employees', invite.employee_id);
    await updateDoc(empRef, {
      invite_status: 'accepted',
      user_id: user.uid,
      updated_at: serverTimestamp(),
    });

    return { success: true, user: userProfile };
  },

  async resend(inviteId) {
    const inviteRef = doc(db, 'invites', inviteId);
    const inviteSnap = await getDoc(inviteRef);

    if (!inviteSnap.exists()) {
      throw new Error('Invite not found');
    }

    const oldInvite = inviteSnap.data();

    // Delete old invite and create new one to trigger email via Firestore trigger
    await deleteDoc(inviteRef);

    // Generate new token and expiry
    const newToken = this.generateToken();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);

    // Create new invite (triggers on_invite_created which sends email)
    const newInvite = {
      token: newToken,
      employee_id: oldInvite.employee_id,
      email: oldInvite.email,
      name: oldInvite.name,
      company_name: oldInvite.company_name,
      app_url: window.location.origin,
      status: 'pending',
      created_at: serverTimestamp(),
      expires_at: expiresAt.toISOString(),
      created_by: getCurrentUserId(),
      resent_from: inviteId, // Track that this was a resend
    };

    await addDoc(collection(db, 'invites'), newInvite);

    // Email is sent automatically via Firestore trigger

    return {
      success: true,
      inviteUrl: `${window.location.origin}/accept-invite/${newToken}`,
      email: oldInvite.email,
      emailSent: true, // Firestore trigger handles this
    };
  },
};

/**
 * Config API - Direct Firestore
 */
export const configApi = {
  async get(orgId) {
    const userId = getCurrentUserId();
    if (!userId) {
      return { success: true, config: {} };
    }

    const docRef = doc(db, 'business_config', userId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { success: true, config: { id: docSnap.id, ...docSnap.data() } };
    }

    return { success: true, config: {} };
  },

  async update(orgId, settings) {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('Not authenticated');
    }

    const docRef = doc(db, 'business_config', userId);

    // Use setDoc with merge to create or update
    await setDoc(docRef, {
      ...settings,
      user_id: userId,
      updated_at: serverTimestamp(),
    }, { merge: true });

    return { success: true, config: settings };
  },
};

/**
 * Simplified API interface for hooks
 */
const firestoreApi = {
  // Dashboard
  getDashboard: () => dashboardApi.getSummary(),

  // Employees (include all statuses, let UI filter)
  getEmployees: () => employeeApi.list(null, true),
  getEmployee: (id) => employeeApi.get(id),
  createEmployee: (data) => employeeApi.create(data),
  updateEmployee: (id, data) => employeeApi.update(id, data),
  deleteEmployee: (id) => employeeApi.delete(id),
  reinstateEmployee: (id) => employeeApi.reinstate(id),

  // Leave requests
  getLeaveRequests: (status) => leaveApi.list(null, status ? { status } : {}),
  getLeaveRequest: (id) => leaveApi.get(id),
  submitLeaveRequest: (data) => leaveApi.create(data),
  approveLeaveRequest: (id, notes) => leaveApi.approve(id, notes),
  denyLeaveRequest: (id, reason) => leaveApi.deny(id, reason),
  cancelLeaveRequest: (id) => leaveApi.cancel(id),

  // Eligibility
  checkEligibility: (employeeId) => leaveApi.getBalance(employeeId),

  // Premiums
  calculatePremiums: (employeeId) => payrollApi.calculatePremiums(null, null),

  // Payroll
  getPayrolls: () => payrollApi.getHistory(),
  processPayroll: (data) => payrollApi.calculatePremiums(null, data),

  // Reports
  getReports: () => reportApi.getHistory(),
  generateDEEDReport: (quarter, year) => reportApi.generate(null, { quarter, year }),

  // Chat
  createChatSession: () => chatApi.startSession(),
  sendChatMessage: (sessionId, message, userRole) => chatApi.sendMessage(sessionId, message, userRole),

  // Invites
  createInvite: (employeeId, email, name, companyName) => inviteApi.create(employeeId, email, name, companyName),
  getInviteByToken: (token) => inviteApi.getByToken(token),
  acceptInvite: (token, password) => inviteApi.accept(token, password),
  resendInvite: (inviteId) => inviteApi.resend(inviteId),

  // Full API access
  employee: employeeApi,
  leave: leaveApi,
  payroll: payrollApi,
  report: reportApi,
  policy: policyApi,
  forecast: forecastApi,
  chat: chatApi,
  dashboard: dashboardApi,
  config: configApi,
  invite: inviteApi,
};

export default firestoreApi;
